// The Reddit Pixel (ad conversion measurement; docs/project/analytics-spec.md): Reddit's install
// snippet, run by web/site/site.ts only once the cookie banner allows advertising. The id is the
// templated meta (config REDDIT_PIXEL_ID); empty = no pixel. The snippet defines a queueing
// window.rdt, loads pixel.js async, and records the PageVisit — one per page, since every site
// route is a full page load. No match keys: init carries no email or other identifier. A
// browser sending Global Privacy Control gets no pixel even after accepting: the signal is an
// opt-out of sharing for cross-context advertising, which the pixel is (privacy.html).

type Rdt = ((...args: unknown[]) => void) & {
  sendEvent?: (...args: unknown[]) => void;
  callQueue: unknown[][];
};

declare global {
  interface Window {
    rdt?: Rdt;
  }
}

// Off once the visitor withdraws advertising on this page: pixel.js stays loaded (a script can't
// be unloaded), so the Lead below must not reach it.
let withdrawn = false;

/** Loads the pixel and records this page's visit; idempotent, no-op without an id. */
export function startRedditPixel(): void {
  withdrawn = false;
  const id = document
    .querySelector('meta[name="featurette-reddit-pixel"]')
    ?.getAttribute("content")
    ?.trim();
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
  if (!id || gpc || window.rdt) return;
  const rdt: Rdt = Object.assign(
    (...args: unknown[]) => {
      if (rdt.sendEvent) rdt.sendEvent(...args);
      else rdt.callQueue.push(args);
    },
    { callQueue: [] as unknown[][] },
  );
  window.rdt = rdt;
  const script = document.createElement("script");
  script.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${encodeURIComponent(id)}`;
  script.async = true;
  document.head.append(script);
  rdt("init", id);
  rdt("track", "PageVisit");
}

/** A guest video request the server accepted, as Reddit's Lead; a no-op unless the pixel
 * started (advertising allowed). The conversion id is random — never the job's token, which
 * opens the visitor's result page. pixel.js posts with `keepalive`, so the navigation to the
 * result page right after does not cancel it. */
export function trackRedditLead(): void {
  if (!withdrawn) window.rdt?.("track", "Lead", { conversionId: crypto.randomUUID() });
}

/** Advertising not allowed: deletes the pixel's first-party cookies (`_rdt_uuid`, `_rdt_cid`),
 * which the server reads as consent for the sign-up it reports (server/reddit.ts), and stops
 * this page's pixel from sending more. pixel.js sets them on the registrable domain, so each
 * parent domain of this host is tried; the browser refuses the ones that are not ours. */
export function stopRedditPixel(): void {
  withdrawn = true;
  const labels = location.hostname.split(".");
  const domains = labels.map((_, i) => labels.slice(i).join(".")).filter((d) => d.includes("."));
  // Absent outside a secure context, and in browsers older than Firefox 140 or Safari 18.4.
  const store: CookieStore | undefined = window.cookieStore;
  for (const name of ["_rdt_uuid", "_rdt_cid"]) {
    for (const domain of [undefined, ...domains]) {
      try {
        if (store) {
          void store.delete({ name, path: "/", ...(domain && { domain }) }).catch(() => {});
        } else {
          // biome-ignore lint/suspicious/noDocumentCookie: the fallback where cookieStore is absent
          document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ""}`;
        }
      } catch {
        // An opaque origin (a sandboxed frame) throws on document.cookie.
      }
    }
  }
}
